import React, { useState, useEffect, useCallback } from 'react';
import { X, Share2, Gift, Users, Check, PartyPopper, UserPlus, Copy, CheckCircle } from 'lucide-react';
import { useTheme, useLanguage, API } from '../App';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import confetti from 'canvas-confetti';
import axios from '../lib/axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Key for storing pending share action intent
const PENDING_SHARE_ACTION_KEY = 'pending_share_action';

const ShareWinPopup = ({ user, onClose, forceShow = false }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [progress, setProgress] = useState({ total_visits: 0, total_signups: 0, target: 3, goal_reached: false, total_tickets: 0, referral_code: '' });
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [comingSoonActive, setComingSoonActive] = useState(false);
  const { theme } = useTheme();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const isDark = theme === 'dark';

  // Check if Coming Soon mode is active
  useEffect(() => {
    const checkComingSoon = async () => {
      try {
        const response = await axios.get(`${API}/admin/coming-soon`);
        setComingSoonActive(response.data?.enabled === true);
      } catch (error) {
        setComingSoonActive(false);
      }
    };
    checkComingSoon();
  }, []);

  // Translations
  const translations = {
    en: {
      winAction: 'Win Promotion',
      winTitle: 'Win 100 XCG Cash! 💰',
      winTitleSuccess: "You're In! 🎉",
      shareText: 'Share with 3 friends to enter the draw!',
      successText: 'Share more for extra chances!',
      progress: 'Progress',
      friends: 'friends',
      friend: 'Friend',
      of: 'of',
      visits: 'visits',
      registrations: 'registrations',
      bonusText: '💡 Registrations = extra tickets!',
      shareWhatsApp: 'Share via WhatsApp',
      copyLink: 'Copy Link',
      copied: 'Copied!',
      done: 'Done',
      later: 'Later',
      neverShow: "Don't show again",
      tickets: 'ticket(s)',
      forDraw: 'for draw',
      viewInProfile: 'View tickets in Profile',
      // Not logged in texts - WIN FOCUS
      needAccount: '🎟️ WIN: 100 XCG CASH',
      needAccountSubtitle: '3 Friends = Your chance to win!',
      needAccountDetail: 'Share with 3 friends and get a chance to win!',
      createAccount: 'Claim My Tickets'
    },
    nl: {
      winAction: 'Win Actie',
      winTitle: 'Win 100 XCG Cash! 💰',
      winTitleSuccess: 'Je doet mee! 🎉',
      shareText: 'Deel met 3 vrienden voor deelname!',
      successText: 'Deel meer voor extra kansen!',
      progress: 'Voortgang',
      friends: 'vrienden',
      friend: 'Vriend',
      of: 'van',
      visits: 'bezoeken',
      registrations: 'registraties',
      bonusText: '💡 Registraties = extra tickets!',
      shareWhatsApp: 'Deel via WhatsApp',
      copyLink: 'Kopieer Link',
      copied: 'Gekopieerd!',
      done: 'Klaar',
      later: 'Later',
      neverShow: 'Niet meer tonen',
      tickets: 'ticket(s)',
      forDraw: 'voor loting',
      viewInProfile: 'Bekijk tickets in Profiel',
      // Not logged in texts - WIN FOCUS
      needAccount: '🎟️ WIN: 100 XCG CASH',
      needAccountSubtitle: '3 Vrienden = Jouw winkans!',
      needAccountDetail: 'Deel met 3 vrienden en maak kans!',
      createAccount: 'Claim Mijn Tickets'
    },
    es: {
      winAction: 'Promoción',
      winTitle: '¡Gana 100 XCG Cash! 💰',
      winTitleSuccess: '¡Estás dentro! 🎉',
      shareText: '¡Comparte con 3 amigos para participar!',
      successText: '¡Comparte más para más chances!',
      progress: 'Progreso',
      friends: 'amigos',
      friend: 'Amigo',
      of: 'de',
      visits: 'visitas',
      registrations: 'registros',
      bonusText: '💡 Registros = tickets extra!',
      shareWhatsApp: 'Compartir por WhatsApp',
      copyLink: 'Copiar Link',
      copied: '¡Copiado!',
      done: 'Listo',
      later: 'Después',
      neverShow: 'No mostrar más',
      tickets: 'ticket(s)',
      forDraw: 'para sorteo',
      viewInProfile: 'Ver tickets en Perfil',
      // Not logged in texts - WIN FOCUS
      needAccount: '🎟️ GANA: 100 XCG CASH',
      needAccountSubtitle: '¡3 Amigos = Tu oportunidad de ganar!',
      needAccountDetail: '¡Comparte con 3 amigos y participa en el sorteo!',
      createAccount: 'Reclamar Mis Tickets'
    }
  };

  const t = translations[language] || translations.en;

  // Fetch referral progress (used by both normal flow and forceShow)
  const fetchProgress = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      // Initialize referral code if needed
      await fetch(`${API_URL}/api/referrals/initialize-referral-code`, {
        method: 'POST',
        credentials: 'include'
      });
      
      // Get progress
      const progressRes = await fetch(`${API_URL}/api/referrals/my-progress`, {
        credentials: 'include'
      });
      
      if (progressRes.ok) {
        const data = await progressRes.json();
        setProgress(data);
      }
    } catch (error) {
      console.error('Failed to fetch referral progress:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Reset dismissed state and show immediately when forceShow becomes true
  useEffect(() => {
    if (forceShow && user) {
      setDismissed(false);
      setLoading(true);
      // Set mobile detection
      setIsMobile(window.innerWidth < 768);
      fetchProgress();
      setIsAnimating(true);
      setTimeout(() => setIsVisible(true), 100);
    }
  }, [forceShow, user, fetchProgress]);

  // Check if popup should show (normal flow, not forceShow)
  useEffect(() => {
    // Skip this check if forceShow is active
    if (forceShow) return;

    const checkShouldShow = () => {
      // Don't show if Coming Soon mode is active
      if (comingSoonActive) {
        return false;
      }
      
      // Don't show on homepage when Coming Soon could be active
      if (location.pathname === '/') {
        return false;
      }
      
      // Don't show on auth-related pages or admin pages
      const excludedPaths = ['/auth', '/register', '/reset-password', '/verify-email', '/login', '/admin'];
      if (excludedPaths.some(path => window.location.pathname.startsWith(path))) {
        return false;
      }
      
      // Check permanent dismiss
      const permanentlyDismissed = localStorage.getItem('sharewin_permanently_dismissed');
      if (permanentlyDismissed === 'true') {
        return false;
      }

      // Check "later" dismiss (24 hour cooldown)
      const laterDismissed = localStorage.getItem('sharewin_later_timestamp');
      if (laterDismissed) {
        const hoursSinceDismiss = (Date.now() - parseInt(laterDismissed)) / (1000 * 60 * 60);
        if (hoursSinceDismiss < 24) {
          return false;
        }
      }

      // Don't show if InstallApp popup might be visible
      const installPopupDismissed = localStorage.getItem('installPopupDismissed');
      const visitCount = parseInt(localStorage.getItem('visitCount') || '0');
      
      if (visitCount >= 3 && !installPopupDismissed) {
        return false;
      }

      return true;
    };

    if (!checkShouldShow()) {
      setDismissed(true);
      return;
    }

    // Detect mobile
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);

    // Fetch progress for logged-in users
    fetchProgress();

    // Show popup after 15 seconds (after InstallAppPopup which shows at 5s)
    const timer = setTimeout(() => {
      setIsAnimating(true);
      setTimeout(() => setIsVisible(true), 100);
    }, 15000);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', checkMobile);
    };
  }, [user, forceShow, fetchProgress, comingSoonActive, location.pathname]);

  // Confetti effect when goal reached
  useEffect(() => {
    if (progress.goal_reached && isVisible) {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
    }
  }, [progress.goal_reached, isVisible]);

  const handleLater = () => {
    localStorage.setItem('sharewin_later_timestamp', Date.now().toString());
    setIsVisible(false);
    setTimeout(() => onClose?.(), 300);
  };

  const handleNeverShow = () => {
    localStorage.setItem('sharewin_permanently_dismissed', 'true');
    setIsVisible(false);
    setTimeout(() => onClose?.(), 300);
  };

  const handleWhatsAppShare = useCallback(() => {
    if (!user || !progress.referral_code) return;
    
    const siteUrl = window.location.origin;
    const refLink = `${siteUrl}/?ref=${progress.referral_code}`;
    
    const messages = {
      en: `Check out this marketplace! I could win 100 XCG Cash if you take a look: ${refLink}`,
      nl: `Check deze marktplaats! Ik maak kans op 100 XCG Cash als jij ook even kijkt: ${refLink}`,
      es: `¡Mira este mercado! Puedo ganar 100 XCG Cash si lo visitas: ${refLink}`
    };
    
    const message = encodeURIComponent(messages[language] || messages.en);
    window.open(`https://wa.me/?text=${message}`, '_blank');
  }, [progress.referral_code, language, user]);

  const handleCopyLink = useCallback(async () => {
    if (!progress.referral_code) return;
    
    const siteUrl = window.location.origin;
    const refLink = `${siteUrl}/?ref=${progress.referral_code}`;
    
    try {
      await navigator.clipboard.writeText(refLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, [progress.referral_code]);

  const handleDone = () => {
    handleLater();
  };

  const handleCreateAccount = () => {
    // Save intent to localStorage so we can show the popup again after login
    localStorage.setItem(PENDING_SHARE_ACTION_KEY, 'true');
    handleLater(); // Dismiss popup
    navigate('/register');
  };

  const handleLogin = () => {
    // Save intent to localStorage so we can show the popup again after login
    localStorage.setItem(PENDING_SHARE_ACTION_KEY, 'true');
    handleLater();
    navigate('/register');
  };

  // Don't render if dismissed or not visible
  if (dismissed || !isVisible) return null;

  const progressPercent = Math.min((progress.total_visits / progress.target) * 100, 100);
  const isLoggedIn = !!user;

  // Show loading spinner while fetching data
  if (loading && isLoggedIn) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="sharewin-popup-loading">
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
        <div className={`relative rounded-3xl p-8 ${isDark ? 'bg-slate-900' : 'bg-white'}`}>
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
            <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  // Mobile Action Sheet Style - Centered for non-logged in, bottom sheet for logged in
  if (isMobile) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="sharewin-popup-mobile">
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={handleLater}
        />

        {/* Centered Card */}
        <div 
          className={`relative w-full max-w-sm transform transition-all duration-300 ease-out ${
            isAnimating ? 'scale-100 opacity-100' : 'scale-90 opacity-0'
          }`}
        >
          {/* Content */}
          <div className={`rounded-3xl overflow-hidden ${isDark ? 'bg-slate-900' : 'bg-white'}`}>
            {/* Close button */}
            <div className="flex items-center justify-between px-5 pt-4 pb-2">
              <span className={`text-xs uppercase tracking-wider font-medium ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                {t.winAction}
              </span>
              <button
                onClick={handleLater}
                className={`p-2 rounded-full transition-all ${isDark ? 'text-white/60 hover:text-white hover:bg-white/10' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
                data-testid="sharewin-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-5 pb-6 text-center">
              {/* Icon with animation for non-logged in users */}
              <div className="relative inline-flex mb-4">
                <div className={`w-20 h-20 rounded-2xl flex items-center justify-center ${
                  !isLoggedIn
                    ? 'bg-gradient-to-br from-amber-400 to-orange-500 animate-pulse'
                    : progress.goal_reached 
                      ? 'bg-gradient-to-br from-green-400 to-emerald-500' 
                      : 'bg-gradient-to-br from-amber-400 to-orange-500'
                }`}>
                  {!isLoggedIn ? (
                    <Gift className="w-10 h-10 text-white animate-bounce" />
                  ) : progress.goal_reached ? (
                    <PartyPopper className="w-10 h-10 text-white" />
                  ) : (
                    <Gift className="w-10 h-10 text-white" />
                  )}
                </div>
                {/* Sparkle effects for non-logged in */}
                {!isLoggedIn && (
                  <>
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-300 rounded-full animate-ping" />
                    <div className="absolute -bottom-1 -left-1 w-2 h-2 bg-orange-300 rounded-full animate-ping" style={{ animationDelay: '0.5s' }} />
                  </>
                )}
              </div>

              {/* Title & Description - Different for logged in vs not */}
              {!isLoggedIn ? (
                <>
                  <h2 className={`text-xl font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {t.needAccount}
                  </h2>
                  <p className={`text-sm font-medium mb-2 ${isDark ? 'text-white/70' : 'text-gray-700'}`}>
                    {t.needAccountSubtitle}
                  </p>
                  <p className={`text-sm mb-4 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                    {t.needAccountDetail}
                  </p>
                </>
              ) : (
                <>
                  <h2 className={`text-xl font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {progress.goal_reached ? t.winTitleSuccess : t.winTitle}
                  </h2>
                  <p className={`text-sm mb-4 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                    {progress.goal_reached 
                      ? t.successText
                      : `${t.shareText} ${t.shareTextDetail}`
                    }
                  </p>
                </>
              )}

              {/* Progress Bar - Only for logged in users */}
              {isLoggedIn && (
                <div className={`mb-4 p-4 rounded-xl border ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-sm font-medium ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                      {t.progress}
                    </span>
                    <span className={`text-sm font-bold ${progress.goal_reached ? 'text-green-500' : 'text-amber-500'}`}>
                      {progress.total_visits}/{progress.target} {t.friends}
                    </span>
                  </div>
                  <div className={`h-3 rounded-full overflow-hidden ${isDark ? 'bg-white/10' : 'bg-gray-200'}`}>
                    <div 
                      className={`h-full rounded-full transition-all duration-500 ${
                        progress.goal_reached 
                          ? 'bg-gradient-to-r from-green-400 to-emerald-500' 
                          : 'bg-gradient-to-r from-amber-400 to-orange-500'
                      }`}
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                  
                  {/* Tickets info */}
                  {progress.total_tickets > 0 && (
                    <div className="mt-3 flex items-center justify-center gap-2">
                      <span className="text-green-500 font-bold">{progress.total_tickets} {t.tickets}</span>
                      <span className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{t.forDraw}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Bonus text - Only for logged in */}
              {isLoggedIn && (
                <p className={`text-xs mb-3 ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`}>
                  {t.bonusText}
                </p>
              )}

              {/* Action Buttons */}
              {!isLoggedIn ? (
                <button
                  onClick={handleCreateAccount}
                  className="w-full py-3.5 px-5 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-bold rounded-xl flex items-center justify-center gap-2 active:scale-[0.98] transition-transform shadow-lg"
                  data-testid="create-account-btn"
                >
                  <UserPlus className="w-5 h-5" />
                  <span>{t.createAccount}</span>
                </button>
              ) : (
                <div className="space-y-2">
                  {/* WhatsApp Share Button */}
                  <button
                    onClick={handleWhatsAppShare}
                    className="w-full py-3 px-4 bg-[#25D366] text-white font-bold rounded-xl flex items-center justify-center gap-2 active:scale-[0.98] transition-transform shadow-lg"
                    data-testid="whatsapp-share-btn"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                    <span>{t.shareWhatsApp}</span>
                  </button>
                  
                  {/* Copy Link & Done Row */}
                  <div className="flex gap-2">
                    <button
                      onClick={handleCopyLink}
                      className={`flex-1 py-2.5 px-3 rounded-xl flex items-center justify-center gap-2 font-medium transition-all ${
                        copied 
                          ? 'bg-green-500/20 text-green-500' 
                          : isDark 
                            ? 'bg-white/10 text-white hover:bg-white/20' 
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      data-testid="copy-link-btn"
                    >
                      {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      <span className="text-sm">{copied ? t.copied : t.copyLink}</span>
                    </button>
                    <button
                      onClick={handleDone}
                      className={`flex-1 py-2.5 px-3 rounded-xl font-medium transition-all ${
                        isDark 
                          ? 'bg-primary text-white hover:bg-primary/90' 
                          : 'bg-primary text-white hover:bg-primary/90'
                      }`}
                      data-testid="done-btn"
                    >
                      {t.done}
                    </button>
                  </div>
                  
                  {/* View in Profile link */}
                  <Link 
                    to="/profile" 
                    onClick={handleDone}
                    className={`block text-center text-xs mt-2 ${isDark ? 'text-white/50 hover:text-white/70' : 'text-gray-400 hover:text-gray-600'}`}
                  >
                    {t.viewInProfile} →
                  </Link>
                </div>
              )}

              {/* Later / Never show - Only for non-logged in */}
              {!isLoggedIn && (
                <div className="mt-4 flex items-center justify-center gap-4">
                  <button
                    onClick={handleLater}
                    className={`text-sm transition-colors ${isDark ? 'text-white/40 hover:text-white/70' : 'text-gray-400 hover:text-gray-600'}`}
                    data-testid="later-btn"
                  >
                    {t.later}
                  </button>
                  <span className={`${isDark ? 'text-white/20' : 'text-gray-300'}`}>|</span>
                  <button
                    onClick={handleNeverShow}
                    className={`text-sm transition-colors ${isDark ? 'text-white/40 hover:text-white/70' : 'text-gray-400 hover:text-gray-600'}`}
                    data-testid="never-show-btn"
                  >
                    {t.neverShow}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Desktop Modal
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="sharewin-popup-desktop">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-md"
        onClick={handleLater}
      />

      {/* Popup Card */}
      <div className={`relative w-full max-w-md transform transition-all duration-500 ${isAnimating ? 'scale-100 opacity-100' : 'scale-90 opacity-0'}`}>
        {/* Floating decorations */}
        <div className={`absolute -top-10 -left-10 w-20 h-20 rounded-full blur-2xl ${!isLoggedIn ? 'bg-blue-500/20' : 'bg-amber-500/20'}`} />
        <div className={`absolute -bottom-10 -right-10 w-24 h-24 rounded-full blur-2xl ${!isLoggedIn ? 'bg-indigo-500/20' : 'bg-orange-500/20'}`} />
        
        {/* Main Card */}
        <div className={`relative backdrop-blur-xl rounded-3xl border shadow-2xl overflow-hidden ${isDark ? 'bg-slate-900/90 border-white/10' : 'bg-white/95 border-gray-200'}`}>
          {/* Top gradient */}
          <div className={`absolute top-0 left-0 right-0 h-1 ${
            !isLoggedIn
              ? 'bg-gradient-to-r from-blue-400 via-indigo-500 to-purple-500'
              : progress.goal_reached 
                ? 'bg-gradient-to-r from-green-400 via-emerald-500 to-teal-500' 
                : 'bg-gradient-to-r from-amber-400 via-orange-500 to-red-500'
          }`} />
          
          {/* Close button */}
          <button
            onClick={handleLater}
            className={`absolute top-4 right-4 p-2 rounded-full transition-all z-10 ${isDark ? 'text-white/60 hover:text-white hover:bg-white/10' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
            data-testid="sharewin-close"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Content */}
          <div className="p-8 text-center relative">
            {/* Icon with animation for non-logged in users */}
            <div className="relative inline-flex mb-6">
              <div className={`w-24 h-24 rounded-2xl flex items-center justify-center shadow-lg ${
                !isLoggedIn
                  ? 'bg-gradient-to-br from-amber-400 to-orange-500 shadow-orange-500/30 animate-pulse'
                  : progress.goal_reached 
                    ? 'bg-gradient-to-br from-green-400 to-emerald-500 shadow-green-500/30' 
                    : 'bg-gradient-to-br from-amber-400 to-orange-500 shadow-orange-500/30'
              }`}>
                {!isLoggedIn ? (
                  <Gift className="w-12 h-12 text-white animate-bounce" />
                ) : progress.goal_reached ? (
                  <PartyPopper className="w-12 h-12 text-white" />
                ) : (
                  <Gift className="w-12 h-12 text-white" />
                )}
              </div>
              {/* Sparkle effects for non-logged in */}
              {!isLoggedIn && (
                <>
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-300 rounded-full animate-ping" />
                  <div className="absolute -bottom-1 -left-1 w-3 h-3 bg-orange-300 rounded-full animate-ping" style={{ animationDelay: '0.5s' }} />
                </>
              )}
              {isLoggedIn && progress.total_tickets > 0 && (
                <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-green-500 flex items-center justify-center shadow-lg text-white font-bold text-sm">
                  {progress.total_tickets}
                </div>
              )}
            </div>

            {/* Title & Description - Different for logged in vs not */}
            {!isLoggedIn ? (
              <>
                <h2 className={`text-2xl font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {t.needAccount}
                </h2>
                <p className={`text-base font-medium mb-2 ${isDark ? 'text-white/70' : 'text-gray-700'}`}>
                  {t.needAccountSubtitle}
                </p>
                <p className={`mb-6 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                  {t.needAccountDetail}
                </p>
              </>
            ) : (
              <>
                <h2 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {progress.goal_reached ? t.winTitleSuccess : t.winTitle}
                </h2>
                <p className={`mb-4 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                  {progress.goal_reached ? t.successText : t.shareText}
                </p>
              </>
            )}

            {/* Progress Section - Only for logged in users */}
            {isLoggedIn && (
              <div className={`mb-6 p-5 rounded-2xl border ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'}`}>
                {/* Progress indicators */}
                <div className="flex items-center justify-center gap-3 mb-4">
                  {[1, 2, 3].map((num) => (
                    <div key={num} className="flex flex-col items-center">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                        num <= progress.total_visits
                          ? progress.goal_reached 
                            ? 'bg-green-500 text-white'
                            : 'bg-amber-500 text-white'
                          : isDark 
                            ? 'bg-white/10 text-white/30'
                            : 'bg-gray-200 text-gray-400'
                      }`}>
                        {num <= progress.total_visits ? (
                          <Check className="w-6 h-6" />
                        ) : (
                          <Users className="w-5 h-5" />
                        )}
                      </div>
                      <span className={`text-xs mt-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                        {t.friend} {num}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Progress bar */}
                <div className={`h-2 rounded-full overflow-hidden ${isDark ? 'bg-white/10' : 'bg-gray-200'}`}>
                  <div 
                    className={`h-full rounded-full transition-all duration-500 ${
                      progress.goal_reached 
                        ? 'bg-gradient-to-r from-green-400 to-emerald-500' 
                        : 'bg-gradient-to-r from-amber-400 to-orange-500'
                    }`}
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>

                {/* Stats */}
                <div className="mt-3 flex items-center justify-between">
                  <span className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                    {progress.total_visits} {t.of} {progress.target} {t.visits}
                  </span>
                  {progress.total_signups > 0 && (
                    <span className="text-sm text-green-500 font-medium">
                      +{progress.total_signups} {t.registrations}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Bonus text - Only for logged in */}
            {isLoggedIn && (
              <p className={`text-sm mb-5 px-4 py-2 rounded-lg ${isDark ? 'bg-cyan-500/10 text-cyan-400' : 'bg-cyan-50 text-cyan-600'}`}>
                {t.bonusText}
              </p>
            )}

            {/* Action Buttons */}
            {!isLoggedIn ? (
              <button
                onClick={handleCreateAccount}
                className="w-full py-4 px-6 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-bold rounded-2xl hover:shadow-xl hover:shadow-blue-500/30 hover:-translate-y-1 transition-all flex items-center justify-center gap-3 group"
                data-testid="create-account-btn"
              >
                <UserPlus className="w-6 h-6" />
                <span>{t.createAccount}</span>
              </button>
            ) : (
              <div className="space-y-3">
                {/* WhatsApp Share Button */}
                <button
                  onClick={handleWhatsAppShare}
                  className="w-full py-3.5 px-5 bg-[#25D366] text-white font-bold rounded-xl hover:shadow-lg hover:shadow-green-500/30 hover:-translate-y-0.5 transition-all flex items-center justify-center gap-3"
                  data-testid="whatsapp-share-btn"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                  <span>{t.shareWhatsApp}</span>
                </button>
                
                {/* Copy Link & Done Row */}
                <div className="flex gap-3">
                  <button
                    onClick={handleCopyLink}
                    className={`flex-1 py-3 px-4 rounded-xl flex items-center justify-center gap-2 font-medium transition-all ${
                      copied 
                        ? 'bg-green-500/20 text-green-500' 
                        : isDark 
                          ? 'bg-white/10 text-white hover:bg-white/20' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    data-testid="copy-link-btn"
                  >
                    {copied ? <CheckCircle className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                    <span>{copied ? t.copied : t.copyLink}</span>
                  </button>
                  <button
                    onClick={handleDone}
                    className="flex-1 py-3 px-4 bg-primary text-white font-semibold rounded-xl hover:bg-primary/90 transition-all"
                    data-testid="done-btn"
                  >
                    {t.done}
                  </button>
                </div>
                
                {/* View in Profile link */}
                <Link 
                  to="/profile" 
                  onClick={handleDone}
                  className={`block text-center text-sm mt-1 ${isDark ? 'text-white/50 hover:text-white/70' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  {t.viewInProfile} →
                </Link>
              </div>
            )}

            {/* Later / Never show - Only for non-logged in */}
            {!isLoggedIn && (
              <div className="mt-5 flex items-center justify-center gap-6">
                <button
                  onClick={handleLater}
                  className={`text-sm transition-colors ${isDark ? 'text-white/40 hover:text-white/70' : 'text-gray-400 hover:text-gray-600'}`}
                  data-testid="later-btn"
                >
                  {t.later}
                </button>
                <span className={`${isDark ? 'text-white/20' : 'text-gray-300'}`}>|</span>
                <button
                  onClick={handleNeverShow}
                  className={`text-sm transition-colors ${isDark ? 'text-white/40 hover:text-white/70' : 'text-gray-400 hover:text-gray-600'}`}
                  data-testid="never-show-btn"
                >
                  {t.neverShow}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export { PENDING_SHARE_ACTION_KEY };
export default ShareWinPopup;
